import { createFileRoute, Link } from '@tanstack/react-router';
import { PublicHeader } from '@/components/public/public-header';
import { PublicFooter } from '@/components/public/public-footer';

export const Route = createFileRoute('/contabilidade-terapeutas')({ component: ContabilidadeMarketing });

/**
 * Página pública — FAQ de contabilidade pra atrair terapeutas/psicólogos
 * que querem entender a estrutura fiscal antes de cadastrar. Conteúdo
 * baseado em docs/NossoEllo_Guia_Contabilidade_Terapeutas.md.
 */
function ContabilidadeMarketing() {
  return (
    <div className="bg-[#FBF8F3] min-h-screen flex flex-col">
      <PublicHeader />
      <main className="flex-1 max-w-3xl mx-auto px-6 py-12 w-full">
        <header className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900">
            Contabilidade para terapeutas e psicólogos
          </h1>
          <p className="text-slate-500 mt-3 max-w-2xl mx-auto">
            Tudo que você precisa saber sobre notas fiscais, regimes tributários e reembolso
            por convênio antes de começar a atender pela Nosso Ello.
          </p>
        </header>

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900 mb-8">
          Este conteúdo é orientativo e <strong>não substitui um contador</strong>. Regras
          variam por município e mudam com frequência.
        </div>

        <FaqItem
          question="Quem emite a nota fiscal: eu ou a Nosso Ello?"
          answer={
            <>
              <p>
                <strong>Você emite a nota do atendimento, sempre.</strong> A Nosso Ello emite
                apenas a nota referente à <strong>taxa de intermediação</strong> que cobra
                de você no fechamento de cada ciclo — isso evita que a plataforma seja
                tributada sobre o valor cheio da consulta.
              </p>
              <p>
                Para facilitar, a nota nominal do atendimento <strong>só é gerada quando o
                cliente solicita</strong> (por exemplo, para pedir reembolso ao plano de saúde
                ou deduzir no IRPF).
              </p>
            </>
          }
        />

        <FaqItem
          question="Meu cliente consegue reembolso pelo convênio?"
          answer={
            <>
              <p>
                <strong>Se você é psicólogo(a) com CRP ativo: sim.</strong> Os planos de
                saúde regulados pela ANS reembolsam atendimento psicológico por livre escolha
                (você não precisa ser credenciado). Basta emitir a nota com os dados corretos.
              </p>
              <p>
                <strong>Se você é terapeuta de outra modalidade (psicanálise, holística,
                integrativa etc.): em regra, não.</strong> Essas modalidades não são reconhecidas
                pela saúde suplementar. Seu cliente pode usar a nota para controle próprio ou
                em benefícios corporativos de bem-estar.
              </p>
            </>
          }
        />

        <FaqItem
          question="Posso ser MEI sendo psicólogo(a)?"
          answer={
            <p>
              <strong>Não.</strong> Profissões regulamentadas (incluindo psicologia) não podem
              aderir ao MEI. A rota indicada para psicólogo(a) com faturamento recorrente é o
              <strong> Simples Nacional (SLU)</strong>, cuidando do Fator R para cair no Anexo III
              (alíquota a partir de ~6%).
            </p>
          }
        />

        <FaqItem
          question="Posso atender como pessoa física (CPF)?"
          answer={
            <>
              <p>
                <strong>Sim</strong>, especialmente no início de carreira ou com baixo volume.
                Você emite NFS-e vinculada ao CPF — em algumas cidades exige inscrição
                municipal de prestador autônomo (CCM).
              </p>
              <p>
                A tributação como autônomo segue a tabela progressiva do IR (7,5% a 27,5%) +
                INSS + ISS. Acima de certo faturamento, vale abrir CNPJ.
              </p>
            </>
          }
        />

        <FaqItem
          question="Sou terapeuta holístico — como funciona pra mim?"
          answer={
            <>
              <p>
                Avalie <strong>MEI</strong> se sua ocupação constar na lista permitida (varia
                e muda — confirmar com contador). Caso contrário, autônomo (CPF) ou Simples
                Nacional conforme o volume.
              </p>
              <p>
                Suas notas <strong>não servem para reembolso de plano de saúde</strong> nem
                como dedução de despesa médica no IRPF do cliente. Isso ficará claramente
                sinalizado para o cliente na sua página de perfil.
              </p>
            </>
          }
        />

        <FaqItem
          question="Como a Nosso Ello me ajuda na emissão?"
          answer={
            <>
              <p>
                Você pode configurar a integração de NFSe diretamente no painel — quando seu
                cliente solicitar a nota, ela é emitida automaticamente com os dados corretos
                (nome e CPF do cliente, descrição padrão, sua categoria).
              </p>
              <p>
                Se não quiser usar a integração, você recebe um aviso por email e tem 7 dias
                úteis para emitir pela sua prefeitura e fazer upload do PDF/XML no painel.
                O cliente baixa direto da plataforma.
              </p>
            </>
          }
        />

        <div className="bg-teal-50 border border-teal-100 rounded-2xl p-6 mt-12 text-center">
          <h2 className="text-xl font-bold text-slate-800">Pronto para começar?</h2>
          <p className="text-slate-600 mt-1 mb-4">
            Crie sua conta de terapeuta e configure tudo em poucos minutos.
          </p>
          <Link
            to="/register"
            className="inline-flex bg-coral-400 hover:bg-coral-500 text-white px-6 py-3 rounded-full font-medium shadow transition"
          >
            Cadastrar como terapeuta
          </Link>
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}

function FaqItem({ question, answer }: { question: string; answer: React.ReactNode }) {
  return (
    <details className="bg-white rounded-xl border border-slate-200 p-5 mb-3 group">
      <summary className="font-bold text-slate-800 cursor-pointer flex items-center justify-between list-none">
        <span>{question}</span>
        <span className="text-teal-500 group-open:rotate-180 transition-transform">▾</span>
      </summary>
      <div className="mt-3 text-sm text-slate-600 leading-relaxed space-y-2">
        {answer}
      </div>
    </details>
  );
}
